import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useRouter } from 'next/router';
import clsx from 'clsx';
import { differenceInDays, format } from 'date-fns';

import styles from './listing-estimate-styles.module.scss';

import { ListingPicker } from '@components/_shared/datePickers/listingPicker';
import { useToggleBodyScroll } from '@utils/hooks/toggleBodyScroll';
import { useClickOutside } from '@utils/hooks/useClickOutside';
import { useIsMobile } from '@utils/hooks/useIsMobile';
import { BaseComponentProps } from '@utils/types/baseComponents';
import { DatesRange } from '@utils/types/calendar';
import {
  SegmentEventNamesEnum,
  SegmentEventPagesEnum,
  useAnalytics,
} from '@utils/hooks/useAnalytics';
import { EstimateUpsellAlternativeType } from '@utils/types/estimate';
import {
  detectMinNightsStayFromGuestyCalendar,
  isRangeFromQueryParametersInvalid,
  prepareArrayOfDisabledDates,
} from '@components/_shared/datePickers/helpers';

const DATE_FORMAT = `MMMM d, yyyy`;

type Props = {
  listingId: string;
  onRangeSelected: (range: DatesRange) => void;
  minAmount: number;
  maxAmount: number;
  nightlyPrices: Record<string, Record<string, number[]>>;
  calendarMinNights: Record<string, Record<string, number[]>>;
  reservedDates: { start: string; end: string }[];
  setUpsellAlternative: (value?: EstimateUpsellAlternativeType) => void;
  upsellAlternative?: EstimateUpsellAlternativeType;
  firstAvailableDate?: string | null;
  lastKnownDay?: string | null;
  withPetFee?: boolean;
} & BaseComponentProps;

ListingEstimateDateRangePicker.defaultProps = {
  minAmount: 0,
  maxAmount: 365,
  reservedDates: [],
  nightlyPrices: {},
  calendarMinNights: {},
  firstAvailableDate: null,
  lastKnownDay: null,
};

export function ListingEstimateDateRangePicker(props: Omit<Props, 'onClick'>) {
  const {
    containerStyle,
    listingId,
    onRangeSelected,
    maxAmount,
    minAmount,
    nightlyPrices,
    calendarMinNights,
    reservedDates,
    firstAvailableDate,
    lastKnownDay,
    upsellAlternative,
    setUpsellAlternative,
    withPetFee,
  } = props;

  const [areInitialDatesApplied, setAreInitialDatesApplied] = useState(false);
  const [showCalendar, setShowCalendar] = useState(false);
  const [selectedDayRange, setSelectedDayRange] = useState<DatesRange>({
    from: null,
    to: null,
  });

  const { toggleBodyScroll, enableScroll } = useToggleBodyScroll();
  const isMobile = useIsMobile();

  const containerRef = useRef<HTMLDivElement>(null);
  const { trackEvent } = useAnalytics();
  const router = useRouter();

  const toggleCalendar = () => {
    if (isMobile) {
      toggleBodyScroll();
    }
    setShowCalendar((showCalendar) => !showCalendar);
  };

  const closeCalendar = () => {
    enableScroll();
    setShowCalendar(false);
  };

  useClickOutside(containerRef, closeCalendar);

  // After a click outside event the selected dates will
  // apply, if a range of dates is selected
  useEffect(() => {
    const { from, to } = selectedDayRange;
    if (!showCalendar && !!onRangeSelected && from && to) {
      onRangeSelected({ from, to });
    }
  }, [showCalendar]);

  const resetDates = () => {
    setSelectedDayRange({ from: null, to: null });
    onRangeSelected({ from: null, to: null });
  };

  const selectDates = () => {
    const { from, to } = selectedDayRange;

    if (!!onRangeSelected && from && to) {
      onRangeSelected({ from, to });
      toggleCalendar();
    }
  };

  const minNightsStay = useMemo(() => {
    return minAmount >= 30
      ? minAmount
      : detectMinNightsStayFromGuestyCalendar(
          selectedDayRange.from,
          calendarMinNights
        ) || minAmount;
  }, [selectedDayRange.from, calendarMinNights]);

  const disabledDays = useMemo(() => {
    return prepareArrayOfDisabledDates(reservedDates, minNightsStay);
  }, [reservedDates, minNightsStay]);

  const onSelectDayRange = (date: DatesRange) => {
    const { from, to } = date;

    setSelectedDayRange({ from, to });

    trackEvent(
      date.to
        ? SegmentEventNamesEnum.CHECK_IN_DATE_APPLIED
        : SegmentEventNamesEnum.CHECK_OUT_DATE_APPLIED,
      {
        PageName: SegmentEventPagesEnum.LISTING_PAGE,
      }
    );
  };

  /**
   *  Setting initial Date Range based on query params.
   *   But it can be selected on the search page w/o respecting the min/max stay.
   *   Then we should ignore it
   */
  useEffect(() => {
    if (
      !areInitialDatesApplied &&
      router.query.dates &&
      !selectedDayRange.from &&
      !selectedDayRange.to
    ) {
      setAreInitialDatesApplied(true);

      const preselectedDateRange = router.query.dates
        ? {
            from: new Date(router.query.dates[0]),
            to: new Date(router.query.dates[1]),
          }
        : undefined;

      if (preselectedDateRange) {
        const isInvalidRange = isRangeFromQueryParametersInvalid(
          preselectedDateRange,
          disabledDays,
          maxAmount,
          minAmount
        );

        if (!isInvalidRange) {
          onSelectDayRange({
            from: new Date(preselectedDateRange.from),
            to: new Date(preselectedDateRange.to),
          });
          onRangeSelected(preselectedDateRange);
        }
      }
    }
  }, [
    router.query,
    selectedDayRange.from,
    selectedDayRange.to,
    areInitialDatesApplied,
  ]);

  const renderCalendar = useMemo(
    () => (
      <ListingPicker
        listingId={listingId}
        disabledDates={disabledDays}
        value={selectedDayRange}
        onApply={selectDates}
        toggleCalendar={toggleCalendar}
        onDatesChange={onSelectDayRange}
        onClearDates={resetDates}
        minNightsStay={minNightsStay}
        maxNightsStay={maxAmount}
        firstAvailableDate={firstAvailableDate}
        lastKnownDay={lastKnownDay}
        nightlyPrices={nightlyPrices}
        upsellAlternative={upsellAlternative}
        setUpsellAlternative={setUpsellAlternative}
        withPetFee={withPetFee}
        withUpsellSuggestions
      />
    ),
    [
      listingId,
      disabledDays,
      selectedDayRange,
      minNightsStay,
      maxAmount,
      firstAvailableDate,
      upsellAlternative,
      withPetFee,
      lastKnownDay,
    ]
  );

  return (
    <div
      ref={containerRef}
      className={clsx([styles.listingEstimateContainer, containerStyle])}
    >
      {isMobile ? (
        <>
          <div className={styles.button} onClick={toggleCalendar}>
            <div className={styles.label}>
              <div>Check-In</div>
              &nbsp;-&nbsp;
              <div>Check-Out</div>
            </div>
            {selectedDayRange.from && selectedDayRange.to ? (
              <div className={styles.dates}>
                {format(selectedDayRange.from, DATE_FORMAT)}
                &nbsp;-&nbsp;
                {format(selectedDayRange.to, DATE_FORMAT)}
                &nbsp;&nbsp;|&nbsp;&nbsp;
                {differenceInDays(selectedDayRange.to, selectedDayRange.from)}
                &nbsp;days
              </div>
            ) : (
              <span className={styles.placeholder}>Select dates</span>
            )}
          </div>
          {showCalendar && renderCalendar}
        </>
      ) : (
        <>
          <div className={styles.button} onClick={toggleCalendar}>
            <div>
              <span className={styles.label}>Check-In</span>
              <div
                className={clsx([
                  styles.dateButton,
                  !!selectedDayRange.from && styles.active,
                ])}
              >
                {selectedDayRange.from
                  ? format(selectedDayRange.from, DATE_FORMAT)
                  : 'Choose date'}
              </div>
            </div>

            <div>
              <span className={styles.label}>Check-Out</span>
              <div
                className={clsx([
                  styles.dateButton,
                  !!selectedDayRange.to && styles.active,
                ])}
              >
                {selectedDayRange.to
                  ? format(selectedDayRange.to, DATE_FORMAT)
                  : 'Choose date'}
              </div>
            </div>
          </div>
          {showCalendar && renderCalendar}
        </>
      )}
    </div>
  );
}
